import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, Palette, BookOpen, Smile, GraduationCap } from "lucide-react";
import WaitlistForm from "@/components/WaitlistForm";

const ComingSoon = () => {
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState("");
  const upcomingFeatures = [
    {
      icon: Palette,
      title: "Special Occasion Coloring Cards",
      description: "Create themed coloring pages for birthdays, holidays, and special celebrations with personalized messages.",
      badge: "Coming Soon"
    },
    {
      icon: BookOpen,
      title: "Mini Storybook Maker",
      description: "Transform your sketched characters into interactive storybooks with custom adventures and narratives.",
      badge: "In Development"
    },
    {
      icon: Smile,
      title: "Cartoonify / Avatar Creator",
      description: "Convert your photos into fun cartoon avatars and stylized character illustrations.",
      badge: "Coming Soon"
    }
  ];

  const handleJoinWaitlist = (feature: string) => {
    setSelectedFeature(feature);
    setIsWaitlistOpen(true);
  };

  return (
    <section className="py-20 px-4 bg-gradient-subtle">
      <div className="container mx-auto">
        {/* Schools & Childcare Hero */}
        <div className="mb-20">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-hero p-12 md:p-16 text-center text-white">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative z-10">
              <div className="flex justify-center mb-6">
                <GraduationCap className="w-16 h-16" />
              </div>
              <h2 className="text-4xl md:text-5xl font-sketch mb-6">
                Skecha for Schools & Childcare
              </h2>
              <p className="text-xl md:text-2xl mb-8 text-white/90 max-w-3xl mx-auto font-comfortable">
                Bulk discounts, educational resources, and classroom management tools designed specifically for educators and childcare providers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Badge variant="secondary" className="text-lg px-6 py-2 bg-white/20 text-white border-white/30">
                  <Clock className="w-4 h-4 mr-2" />
                  Coming Very Soon
                </Badge>
                <Button 
                  variant="secondary" 
                  size="lg"
                  className="bg-white text-primary hover:bg-white/90 font-comfortable"
                  onClick={() => handleJoinWaitlist("Skecha for Schools & Childcare")}
                >
                  Join Waitlist
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Other Features */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-sketch mb-4">
            More Amazing Features Coming Soon
          </h2>
          <p className="text-lg text-muted-foreground font-comfortable max-w-2xl mx-auto">
            We're constantly working on new ways to make creativity more fun and accessible for everyone.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {upcomingFeatures.map((feature, index) => (
            <Card key={index} className="relative overflow-hidden group hover:shadow-creative transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-8 text-center">
                <div className="absolute top-4 right-4">
                  <Badge variant="outline" className="text-xs">
                    {feature.badge}
                  </Badge>
                </div>
                <div className="flex justify-center mb-6">
                  <div className="p-4 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                    <feature.icon className="w-8 h-8 text-primary" />
                  </div>
                </div>
                <h3 className="text-xl font-sketch mb-4">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground font-comfortable leading-relaxed">
                  {feature.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-sm text-muted-foreground font-comfortable mb-6">
            Want to be the first to know when these features launch?
          </p>
          <Button 
            variant="outline" 
            size="lg" 
            className="font-comfortable"
            onClick={() => handleJoinWaitlist("new features")}
          >
            <Clock className="w-4 h-4 mr-2" />
            Get Notified
          </Button>
        </div>
      </div>
      
      <WaitlistForm 
        isOpen={isWaitlistOpen}
        onClose={() => setIsWaitlistOpen(false)}
        feature={selectedFeature}
      />
    </section>
  );
};

export default ComingSoon;
